import { Injectable, Type } from '@angular/core';
import { ColDef, Column, GridApi, GridOptions, GridReadyEvent, IRowNode, RowDataTransaction, RowNode, StartEditingCellParams } from 'ag-grid-community';
import _ from 'underscore';
import { IObject } from '@type_definitions/datatypes';
import { AgActionButtonRendererComponent } from './ag-action-button/ag-action-button-renderer.component';
import { AgButtonRendererComponent } from './ag-button/ag-button-renderer.component';
import { AgButtonDropdownRendererComponent } from './ag-button-dropdown/ag-button-dropdown-renderer.component';
import { AgSelectHeaderComponent } from './ag-select-header.component';
import { AgReportDocumentRendererComponent } from './ag-report-document-renderer.component';
import { AgRichTextRendererComponent } from './ag-rich-text/ag-rich-text-renderer.component';
import { AgContactInfoRendererComponent } from './ag-contact-info/ag-contact-info-renderer.component';
import { AgDetailComponent } from './ag-detail.component';
import { AgTooltipComponent } from './ag-tooltip.component';
import { AgCellEditorComponent } from './ag-cell-editor/ag-cell-editor.component';
import { AgPaginationComponent } from './ag-pagination/ag-pagination.component';
import { CellRendererComp } from './ga-ag-grid.component';

export enum AgGridDomLayout {
  PRINT = 'print',
  AUTO_HEIGHT = 'autoHeight',
  NORMAL = 'normal'
}

export interface IGridInstance {
  gridOptions?:GridOptions;
  api?:GridApi;
}

@Injectable()
export class GaAgGridService {
  private gridInstances:IObject<IGridInstance> = {};

  public install(gridId:string, gridOptions:GridOptions):IGridInstance {
    if (!gridId || !gridOptions) {
      throw new Error('Missing required parameters [gridId, gridOptions] to install grid');
    }

    // save callback coming from gridOptions.onGridReady
    const gridReadyCallback:(event:GridReadyEvent) => void = gridOptions.onGridReady;

    // extend onGridReady callback method to store grid api
    gridOptions.onGridReady = (event:GridReadyEvent):void => {
      this.onGridReadyCallback(gridId, event);

      if (gridReadyCallback) {
        gridReadyCallback(event);
      }
    };

    gridOptions.defaultColDef = gridOptions.defaultColDef || {};
    // do not specify cell data type for better data formatting
    if (_.isUndefined(gridOptions.defaultColDef.cellDataType)) {
      gridOptions.defaultColDef.cellDataType = false;
    }

    this.gridInstances[gridId] = {
      gridOptions,
      api: undefined
    };

    return this.gridInstances[gridId];
  }

  public getGridInstance(gridId:string):IGridInstance {
    if (!gridId) {
      return;
    }

    return this.gridInstances[gridId];
  }

  public onFilterChanged(gridId:string):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      gridApi.onFilterChanged();
    }
  }

  public setGridLoading(gridId:string, isLoading:boolean):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      isLoading ? gridApi.showLoadingOverlay() : (gridApi.getDisplayedRowCount() ? gridApi.hideOverlay() : gridApi.showNoRowsOverlay());
    }
  }

  public highlightGridRow(gridId:string, rowId:number|string):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      const rowIndex:number = gridApi.getRowNode(rowId?.toString())?.rowIndex;

      if (_.isNumber(rowIndex) && rowIndex >= 0) {
        gridApi.ensureIndexVisible(rowIndex);
        gridApi.getDisplayedRowAtIndex(rowIndex).setSelected(true, true);
      }
    }
  }

  public getStatusBarComponentInstance<T>(gridId:string, key:string):T {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      return gridApi.getStatusPanel(key);
    }
  }

  public getDisplayedRowCount(gridId:string):number {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      return gridApi.getDisplayedRowCount();
    }

    return 0;
  }

  public getDisplayedRowAtIndex(gridId:string, index:number):IRowNode {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      return gridApi.getDisplayedRowAtIndex(index);
    }
  }

  public getColumn(gridId:string, key:string | Column):Column {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      return gridApi.getColumn(key);
    }
  }

  public getColumns(gridId:string):Column[] {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      return gridApi.getColumns();
    }
  }

  public setRowData(gridId:string, data:any[], refresh?:boolean):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      gridApi.setGridOption('rowData', data);

      if (refresh) {
        gridApi.redrawRows();
      }
    }
  }

  public setPinnedBottomRowData(gridId:string, data:any[]):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      gridApi.setGridOption('pinnedBottomRowData', data);
    }
  }

  public setColumnDefs(gridId:string, colDefs:ColDef[]):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      gridApi.setGridOption('columnDefs', colDefs);
    }
  }

  public forEachNode(gridId:string, callback:(rowNode:RowNode, index?:number) => void, includeFooterNodes?:boolean):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      gridApi.forEachNode(callback, includeFooterNodes);
    }
  }

  public setColumnsVisible(gridId:string, columnIds:string[], isVisible:boolean):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      gridApi.setColumnsVisible(columnIds, isVisible);
    }
  }

  public sizeColumnsToFit(gridId:string):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      gridApi.sizeColumnsToFit();
    }
  }

  public autoSizeColumns(gridId:string, columnsIds?:string[] | Column[], skipHeader?:boolean):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      if (columnsIds) {
        gridApi.autoSizeColumns(columnsIds, skipHeader);
      } else {
        gridApi.autoSizeAllColumns(skipHeader);
      }
    }
  }

  public redrawRows(gridId:string):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      gridApi.redrawRows();
    }
  }

  public applyTransaction(gridId:string, rowDataTransaction:RowDataTransaction):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      gridApi.applyTransaction(rowDataTransaction);
    }
  }

  public startEditing(gridId:string, params:StartEditingCellParams):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      gridApi.startEditingCell(params);
    }
  }

  public stopEditing(gridId:string, cancel?:boolean):void {
    const gridApi:GridApi = this.getGridInstance(gridId)?.api;

    if (gridApi) {
      gridApi.stopEditing(cancel);
    }
  }

  private onGridReadyCallback(gridId:string, event:GridReadyEvent):void {
    this.setGridApi(gridId, event.api);
  }

  private setGridApi(gridId:string, gridApi:GridApi):void {
    const gridInstance:IGridInstance = this.getGridInstance(gridId);

    if (gridInstance) {
      gridInstance.api = gridApi;
    }
  }

  public getCustomAgGridComponents(gridOptions:GridOptions):IObject<Type<any>> {
    // pre-defined ag-grid components
    let components:IObject<Type<any>>  = {
      [CellRendererComp.ACTION_BUTTON_RENDERER]: AgActionButtonRendererComponent,
      [CellRendererComp.BUTTON_RENDERER]: AgButtonRendererComponent,
      [CellRendererComp.BUTTON_DROPDOWN_RENDERER]: AgButtonDropdownRendererComponent,
      [CellRendererComp.SELECT_HEADER]: AgSelectHeaderComponent,
      [CellRendererComp.REPORT_DOCUMENT_RENDERER]: AgReportDocumentRendererComponent,
      [CellRendererComp.RICH_TEXT_RENDERER]: AgRichTextRendererComponent,
      [CellRendererComp.CONTACT_INFO_RENDERER]: AgContactInfoRendererComponent,
      [CellRendererComp.DETAIL]: AgDetailComponent,
      [CellRendererComp.TOOLTIP]: AgTooltipComponent,
      [CellRendererComp.CELL_EDITOR]: AgCellEditorComponent,
      [CellRendererComp.PAGINATION]: AgPaginationComponent
    };

    // apply framework components defined on grid options
    if (gridOptions.components) {
      components = {
        ...components,
        ...gridOptions.components
      };
    }

    return components;
  }

  public destroyGridInstance(gridId:string = 'default'):void {
    if (!gridId) {
      return;
    }

    delete this.gridInstances[gridId];
  }
}